/** @format */

import { AuthLayout, HomeLayout } from "components";
import Login from "pages/auth/Login";
import { Dashboard } from "pages/dashboard";
import { ProjectForm, ProjectDetail, Projects } from "pages/projects";
import { DepartmentObjective, EmployeeObjective } from "pages/objectives";
import { AccountCodes } from "pages/account-codes";
import { PaymentVouchers, PaymentVoucherDetail } from "pages/payment-vouchers";
import { PurchaseOrders, PurchaseOrderDetail } from "pages/purchase-orders";
import { PaymentBankings, PaymentBankingDetail } from "pages/payment-bankings";
import { BankAccounts } from "pages/bank-accounts";
import { OpeningBalance } from "pages/opening-balance";
import { Companies } from "pages/companies";
import { Customers } from "pages/customers";
import { Units } from "pages/units";
import { OpeningInventories } from "pages/opening-inventories";
import { Receipts, ReceiptDetail } from "pages/receipts";
import { ReceiptBankings, ReceiptBankingDetail } from "pages/receipt-bankings";
import { ReturnOrders, ReturnOrderDetail } from "pages/return-orders";
import { Settings } from "pages/settings";
import { Tasks } from "pages/tasks";
import { Users } from "pages/users";
import WmsCodes from "pages/wms-codes/WmsCodes";
import { WmsItemGroups } from "pages/wms-item-groups";
import { WmsItems } from "pages/wms-items";
import { SaleOrders, SaleOrderDetail } from "pages/sale-orders";
import { OtherOrderDetail, OtherOrders } from "pages/other-orders";
import {
  S1Report,
  S2ReportPage,
  S3Report,
  S4Report,
  S5Report,
  S6Report,
  S7Report,
} from "pages/reports";
import {
  RpInOutReport,
  RpAccountReceivable,
  RpAccountReceivableDetail,
  RpDownGTGT,
  RpHKDState,
  RpPurchaseDetail,
} from "pages/reports/admin";
import { FC, ReactElement, ReactNode } from "react";
import { ROUTES } from "utils";

type TComponent = FC<{
  children: string | ReactElement | ReactElement[] | ReactNode;
}>;

export interface RouterType {
  path: string;
  component: FC;
  layout: TComponent;
}

const publicRoutes: RouterType[] = [
  {
    path: ROUTES.PUBLIC_URL.LOGIN,
    component: Login,
    layout: AuthLayout as TComponent,
  },
];

const privateRoutes: RouterType[] = [
  {
    path: ROUTES.PRIVATE_ROOT.DASHBOARD,
    component: Dashboard,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.TASKS,
    component: Tasks,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.PROJECTS,
    component: Projects,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.PROJECTS_CREATE,
    component: ProjectForm,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.PRIVATE_ROOT.PROJECTS_DETAIL}/:id`,
    component: ProjectDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.USERS,
    component: Users,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.SETTINGS,
    component: Settings,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.ACCOUNT_CODES,
    component: AccountCodes,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.CASH_PAYMENT,
    component: PaymentVouchers,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.CASH_PAYMENT_CREATE,
    component: PaymentVoucherDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.CASH_PAYMENT_DETAIL}/:id`,
    component: PaymentVoucherDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.COMPANIES,
    component: Companies,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.CUSTOMERS,
    component: Customers,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.OPENING_DEBT,
    component: OpeningBalance,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.OPENING_INVENTORY,
    component: OpeningInventories,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.CASH_RECEIPT,
    component: Receipts,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.CASH_RECEIPT_CREATE,
    component: ReceiptDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.CASH_RECEIPT_DETAIL}/:id`,
    component: ReceiptDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.BANK_RECEIPT,
    component: ReceiptBankings,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.BANK_RECEIPT_CREATE,
    component: ReceiptBankingDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.BANK_RECEIPT_DETAIL}/:id`,
    component: ReceiptBankingDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.BANK_PAYMENT,
    component: PaymentBankings,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.BANK_PAYMENT_CREATE,
    component: PaymentBankingDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.BANK_PAYMENT_DETAIL}/:id`,
    component: PaymentBankingDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.BANK_ACCOUNTS,
    component: BankAccounts,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.PURCHASE_DOCUMENT,
    component: PurchaseOrders,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.PURCHASE_DOCUMENT_CREATE,
    component: PurchaseOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.PURCHASE_DOCUMENT_DETAIL}/:id`,
    component: PurchaseOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.PURCHASE_RETURN,
    component: ReturnOrders,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.PURCHASE_RETURN_CREATE,
    component: ReturnOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.PURCHASE_RETURN_DETAIL + "/:id",
    component: ReturnOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.SALES_DOCUMENT,
    component: SaleOrders,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.SALES_DOCUMENT_CREATE,
    component: SaleOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.SALES_DOCUMENT_DETAIL}/:id`,
    component: SaleOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.OTHER_DOCUMENT,
    component: OtherOrders,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.ACCOUNTING.OTHER_DOCUMENT_CREATE,
    component: OtherOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: `${ROUTES.ACCOUNTING.OTHER_DOCUMENT_DETAIL}/:id`,
    component: OtherOrderDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S1A_HKD,
    component: S1Report,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S2_HKD,
    component: S2ReportPage,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S3A_HKD,
    component: S3Report,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S4_HKD,
    component: S4Report,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S5_HKD,
    component: S5Report,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S6_HKD,
    component: S6Report,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_S7_HKD,
    component: S7Report,
    layout: HomeLayout as TComponent,
  },
  // ========= Admin Reports =========
  {
    path: ROUTES.REPORTS.RP_IN_OUT,
    component: RpInOutReport,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_ACCOUNT_RECEIVABLE,
    component: RpAccountReceivable,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_ACCOUNT_RECEIVABLE_DETAIL,
    component: RpAccountReceivableDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_DOWN_GTGT_TAX,
    component: RpDownGTGT,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_HKD_STATE,
    component: RpHKDState,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.REPORTS.RP_PURCHURE_PRODUCT_DETAIL,
    component: RpPurchaseDetail,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.WMS_CODES,
    component: WmsCodes,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.WMS_ITEM_GROUPS,
    component: WmsItemGroups,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.WMS_ITEMS,
    component: WmsItems,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.OBJECTIVE_CUSTOMER_OR_SUPLIES,
    component: Customers,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.OBJECTIVE_EMPLOYEE,
    component: EmployeeObjective,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.OBJECTIVE_DEPARTMENT,
    component: DepartmentObjective,
    layout: HomeLayout as TComponent,
  },
  {
    path: ROUTES.PRIVATE_ROOT.OBJECTIVE_UNIT,
    component: Units,
    layout: HomeLayout as TComponent,
  },
];

export { privateRoutes, publicRoutes };
